import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMarketing, SMSTemplate } from "../../../context/MarketingContext";
import { formatApiErrors } from "../../../services/marketingTemplatesApi";
import { Input } from "../../../components/ui/input2";
import { Label } from "../../../components/ui/Label";
import { Textarea } from "../../../components/ui/Textarea";
import { Button } from "../../../components/ui/Button";
import { useToast } from "../../../hooks/useToast";

const DEFAULT_MAX_LENGTH = 160;

interface SMSTemplateFormProps {
  template?: SMSTemplate | null;
}

export const SMSTemplateForm: React.FC<SMSTemplateFormProps> = ({ template }) => {
  const isEdit = Boolean(template);
  const navigate = useNavigate();
  const toast = useToast();
  const { addSMSTemplate, updateSMSTemplate } = useMarketing();

  const maxLen = useMemo(
    () => Math.max(1, template?.charLimit ?? DEFAULT_MAX_LENGTH),
    [template?.charLimit],
  );

  const [name, setName] = useState(template?.name ?? "");
  const [text, setText] = useState(template?.text ?? "");
  const [errors, setErrors] = useState<{ name?: string; text?: string }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(template?.name ?? "");
    setText(template?.text ?? "");
  }, [template?.id, template?.name, template?.text]);

  const remaining = maxLen - text.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!name.trim()) {
      nextErrors.name = "SMS template name is required.";
    }
    if (!text.trim()) {
      nextErrors.text = "SMS text is required.";
    } else if (text.length > maxLen) {
      nextErrors.text = `SMS text must be ${maxLen} characters or fewer.`;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      if (isEdit && template) {
        await updateSMSTemplate(template.id, {
          name: name.trim(),
          text,
          charLimit: maxLen,
        });
        toast.success("SMS template updated.");
      } else {
        await addSMSTemplate({
          name: name.trim(),
          text,
          charLimit: maxLen,
        });
        toast.success("SMS template created.");
      }
      navigate("/marketing/sms-templates");
    } catch (err) {
      toast.error(formatApiErrors(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-5"
    >
      <div className="space-y-1.5">
        <Label htmlFor="sms-name">SMS Template Name</Label>
        <Input
          id="sms-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Appointment Reminder"
        />
        {errors.name && (
          <p className="text-xs text-red-500 mt-1">{errors.name}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="sms-text">Text</Label>
          <span
            className={`text-[11px] font-medium ${
              remaining < 0 ? "text-red-500" : "text-gray-500"
            }`}
          >
            {remaining} characters left
          </span>
        </div>
        <Textarea
          id="sms-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={maxLen}
          placeholder="Write the SMS body your customers will receive."
          resize="vertical"
          error={errors.text}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/marketing/sms-templates")}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
        >
          {saving ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save Changes" : "Create Template"}
        </Button>
      </div>
    </form>
  );
};

export default SMSTemplateForm;

